import type { CatalogueDemarrage, ConditionEtape, EtapeCatalogue, OptionQuestion } from './catalogue.js';

/** Avancement d'une étape pour un dossier, tel que l'API le rend (L64). */
export interface AvancementEtape {
  codeEtape: string;
  versionCatalogue: string;
  /** null : à faire (jamais faite, ou rouverte). */
  etat: 'fait' | 'sans_objet' | null;
  faitLe: string | null;
  /** Nom de la personne qui a marqué l'étape. */
  faitPar: string | null;
  reponse: string | null;
  motif: string | null;
  /** Point 4 : conditions de passage cochées à la main. */
  conditionsCochees?: readonly string[];
}

export type Statut = 'fait' | 'sans_objet' | 'en_cours' | 'a_venir';

export interface ResumePhase {
  code: string;
  libelle: string;
  etapes: readonly EtapeCatalogue[];
  terminees: number;
  terminee: boolean;
  enCours: boolean;
}

export interface Resume {
  statuts: Readonly<Record<string, Statut>>;
  courante: EtapeCatalogue | null;
  /** Réponse qui met fin à la mission (avocat partenaire), si elle est enregistrée sur une étape faite, avec son étape. */
  finDeMission: { etape: EtapeCatalogue; option: OptionQuestion } | null;
  phases: readonly ResumePhase[];
  casParticulier: readonly EtapeCatalogue[];
  terminees: number;
  total: number;
}

export const parCode = (avancement: readonly AvancementEtape[]): ReadonlyMap<string, AvancementEtape> =>
  new Map(avancement.map((a) => [a.codeEtape, a]));

export function optionChoisie(etape: EtapeCatalogue, reponse: string | null | undefined): OptionQuestion | null {
  return etape.question?.options.find((o) => o.code === reponse) ?? null;
}

/**
 * Étape en cours = la première étape du parcours (hors cas particulier) ni faite ni sans objet. Une réponse de fin de mission
 * (moins de deux tiers à l'attestation) enregistrée sur une étape faite arrête le parcours : plus aucune étape en cours.
 */
export function resumer(cat: CatalogueDemarrage, avancement: readonly AvancementEtape[]): Resume {
  const lus = parCode(avancement);
  const close = (e: EtapeCatalogue) => lus.get(e.code)?.etat != null;
  const parcours = cat.etapes.filter((e) => e.phase !== cat.casParticulier.code);
  let finDeMission: Resume['finDeMission'] = null;
  for (const e of parcours) {
    const a = lus.get(e.code);
    const o = a?.etat === 'fait' ? optionChoisie(e, a.reponse) : null;
    if (o?.finDeMission) {
      finDeMission = { etape: e, option: o };
      break;
    }
  }
  const courante = finDeMission ? null : (parcours.find((e) => !close(e)) ?? null);
  const statuts: Record<string, Statut> = {};
  for (const e of cat.etapes) {
    const etat = lus.get(e.code)?.etat;
    statuts[e.code] = etat === 'fait' ? 'fait' : etat === 'sans_objet' ? 'sans_objet' : e === courante ? 'en_cours' : 'a_venir';
  }
  const phases = cat.phases.map((p) => {
    const etapes = cat.etapes.filter((e) => e.phase === p.code);
    const terminees = etapes.filter(close).length;
    return {
      code: p.code,
      libelle: p.libelle,
      etapes,
      terminees,
      terminee: terminees === etapes.length,
      enCours: courante?.phase === p.code,
    };
  });
  return {
    statuts,
    courante,
    finDeMission,
    phases,
    casParticulier: cat.etapes.filter((e) => e.phase === cat.casParticulier.code),
    terminees: cat.etapes.filter(close).length,
    total: cat.etapes.length,
  };
}

/** « 0 étape », « 1 étape », « 8 étapes » : le singulier vaut pour zéro et un. */
export const etapes = (n: number): string => `${n} étape${n > 1 ? 's' : ''}`;

/** JJ/MM, à l'heure de la Guadeloupe. */
export const jourMois = (iso: string): string =>
  new Date(iso).toLocaleDateString('fr-FR', { timeZone: 'America/Guadeloupe', day: '2-digit', month: '2-digit' });

/** Version du catalogue (« 2026-10-30.1 ») lue comme une date (« 30/10/2026 »). */
export const dateDeVersion = (version: string): string => {
  const [a, m, j] = version.slice(0, 10).split('-');
  return `${j}/${m}/${a}`;
};

export const euros = (n: number): string => `${new Intl.NumberFormat('fr-FR').format(n)} €`;

/** Forçage tracé tel que l'API le rend (couloir G, point 5). */
export interface ForcageLu {
  id: string;
  portee: 'etape' | 'mandat_recherche';
  codeEtape: string | null;
  conditions: readonly string[];
  motif: string;
  forcePar: string;
  forceLe: string;
}

/** Ce que l'on sait du dossier pour évaluer les conditions de passage. */
export interface FaitsDossier {
  /** null : les mandats n'ont pas pu être lus. */
  mandatRechercheSigne: boolean | null;
  forcages: readonly ForcageLu[];
  /** Nombre de personnes de l'arbre du dossier ; null si l'arbre n'a pas pu être lu. */
  personnesArbre?: number | null;
  /** Nombre de personnes de l'arbre au rôle « auteur » (le défunt) ; null si l'arbre n'a pas pu être lu. */
  defuntsArbre?: number | null;
  /** Conditions cochées à la main pour l'étape évaluée. */
  cochees?: readonly string[];
}

export type EtatCondition = 'oui' | 'non' | 'forcee';
export interface ConditionEvaluee {
  condition: ConditionEtape;
  etat: EtatCondition;
}

/** Libellé de la condition légale de l'étape « héritier introuvable » sans mandat (réponse « recherche interdite »). */
export const CONDITION_MANDAT_RECHERCHE = 'Mandat de recherche d’héritiers signé au dossier';

/**
 * Point 4 : chaque condition est évaluée sur les vraies données du dossier. Le mandat : signé dans Documents (« oui »), ou blocage
 * levé par un forçage de la dirigeante (« forcee »). Les autres évaluations arrivent avec leurs lectures ; d'ici là, « non ».
 */
export function evaluerConditions(etape: EtapeCatalogue, faits: FaitsDossier): ConditionEvaluee[] {
  const mandatForce = faits.forcages.some((f) => f.portee === 'mandat_recherche');
  return (etape.conditions ?? []).map((condition) => {
    if (condition.evaluation === 'mandat_recherche_signe')
      return { condition, etat: faits.mandatRechercheSigne === true ? 'oui' : mandatForce ? 'forcee' : 'non' };
    if (condition.evaluation === 'personne_arbre') return { condition, etat: (faits.personnesArbre ?? 0) > 0 ? 'oui' : 'non' };
    if (condition.evaluation === 'defunt_renseigne') return { condition, etat: (faits.defuntsArbre ?? 0) > 0 ? 'oui' : 'non' };
    return { condition, etat: faits.cochees?.includes(condition.code) ? 'oui' : 'non' };
  });
}

/** Libellés de ce qui manque pour passer l'étape, avec la condition légale de la réponse choisie le cas échéant. */
export function manquantes(etape: EtapeCatalogue, option: OptionQuestion | null, faits: FaitsDossier): string[] {
  const liste = evaluerConditions(etape, faits)
    .filter((c) => c.etat === 'non')
    .map((c) => c.condition.libelle);
  if (option?.rechercheInterdite) liste.push(CONDITION_MANDAT_RECHERCHE);
  return liste;
}
